import { Outlet, useNavigate } from 'react-router-dom';
import { SiteHeader, SiteFooter } from '@/components/layout/v3';
import { useAuthStore } from '@/stores/authStore';
import { cognitoAuthService } from '@/services/auth/cognito';
import { clearPendingProfileFor } from '@/lib/pendingProfile';

interface LayoutProps {
  children?: React.ReactNode;
}

/**
 * Main authenticated layout wrapper. Uses the V3 SiteHeader in its
 * authenticated variant (Upload + History nav + user-menu dropdown
 * with Sign Out) so the in-app chrome matches the landing page.
 *
 * SiteHeader is `position: fixed` (h-14) — pt-14 on main keeps
 * page content from rendering under it.
 */
export function Layout({ children }: LayoutProps) {
  const { user, isAuthenticated, signOut } = useAuthStore();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    // On a shared computer, signing out means "I'm done": this account's own
    // unsent questionnaire answers go with it; anyone else's stay.
    clearPendingProfileFor(user?.email);
    try {
      await cognitoAuthService.signOut();
    } catch {
      // Sign out locally even if Cognito call fails
    }
    signOut();
    navigate('/');
  };

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader
        isAuthenticated={isAuthenticated}
        userName={user?.name}
        onSignOut={handleSignOut}
      />
      <main className="flex-1 pt-14">{children || <Outlet />}</main>
      <SiteFooter />
    </div>
  );
}
